import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLazyQuery } from '@apollo/client';
import { Mail, Briefcase, Building2 } from 'lucide-react';
import { GET_EMPLOYEE_BASIC_INFO } from '../../graphql/queries';

interface EmployeeBasicInfo {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  department?: string | null;
  roleName?: string | null;
  position?: {
    id: string;
    name: string;
  } | null;
}

interface EmployeePopoverProps {
  employeeId: string;
  /** Optional eagerly available name; shown immediately while data loads. */
  fallbackName?: string;
  children: React.ReactNode;
  /** Where to anchor the popover relative to the trigger. Default: bottom. */
  side?: 'top' | 'bottom';
}

export const EmployeePopover: React.FC<EmployeePopoverProps> = ({
  employeeId,
  fallbackName,
  children,
  side = 'bottom',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const [fetchEmployee, { data, loading }] = useLazyQuery<{
    employee: EmployeeBasicInfo | null;
  }>(GET_EMPLOYEE_BASIC_INFO, {
    variables: { id: employeeId },
    fetchPolicy: 'cache-first',
  });

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popoverWidth = 256; // w-64
    const margin = 8;
    let left = rect.left;
    if (left + popoverWidth > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - popoverWidth - margin);
    }
    const top =
      side === 'top'
        ? rect.top - margin
        : rect.bottom + margin;
    setPosition({ top, left });
  }, [isOpen, side]);

  useEffect(() => {
    if (!isOpen) return;
    fetchEmployee();

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, fetchEmployee]);

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen((open) => !open);
  };

  const employee = data?.employee;
  const displayName = employee
    ? `${employee.firstName} ${employee.lastName}`
    : fallbackName || '...';

  return (
    <>
      <div
        ref={triggerRef}
        onClick={handleTriggerClick}
        onPointerDown={(e) => e.stopPropagation()}
        className="inline-block cursor-pointer"
      >
        {children}
      </div>
      {isOpen && position &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              position: 'fixed',
              top: side === 'top' ? undefined : position.top,
              bottom: side === 'top' ? window.innerHeight - position.top : undefined,
              left: position.left,
            }}
            className="z-[100] w-64 bg-popover border border-border rounded-md shadow-lg p-3 text-sm"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="font-semibold text-foreground mb-2">
              {displayName}
            </div>
            {loading && !employee && (
              <div className="text-muted-foreground text-xs">Wczytywanie...</div>
            )}
            {employee && (
              <div className="space-y-1.5 text-xs text-muted-foreground">
                {employee.position?.name && (
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{employee.position.name}</span>
                  </div>
                )}
                {employee.department && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{employee.department}</span>
                  </div>
                )}
                {employee.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5 shrink-0" />
                    <a
                      href={`mailto:${employee.email}`}
                      className="text-primary hover:underline truncate"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {employee.email}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};
